/**
 * Phone helpers for the candidate apply flow.
 *
 * VHire is India-first, so the UX defaults to +91 and keeps India's strict
 * 10-digit rule — but a candidate from anywhere should be able to apply. The
 * apply flow historically hardcoded a `+91` prefix and stored a bare national
 * number (no country code), which silently mislabeled every non-Indian number
 * and hard-blocked anyone whose national number wasn't exactly 10 digits.
 *
 * The model here: keep the dial code and the national number as separate UI
 * state, and persist the combined E.164 string (e.g. `+14155550123`) so the
 * stored value is unambiguous and round-trips cleanly to the candidate profile
 * and the recruiter pipeline.
 */

export interface DialCode {
  /** E.164 country calling code, including the leading '+'. */
  code: string;
  flag: string;
  label: string;
}

/**
 * Curated, India-first list covering VHire's primary market plus the largest
 * diaspora / hiring corridors. Intentionally short — a full ITU list would be
 * noise for this audience. Order is the dropdown order.
 */
export const DIAL_CODES: DialCode[] = [
  { code: '+91', flag: '🇮🇳', label: 'India' },
  { code: '+1', flag: '🇺🇸', label: 'USA / Canada' },
  { code: '+44', flag: '🇬🇧', label: 'United Kingdom' },
  { code: '+971', flag: '🇦🇪', label: 'UAE' },
  { code: '+65', flag: '🇸🇬', label: 'Singapore' },
  { code: '+61', flag: '🇦🇺', label: 'Australia' },
  { code: '+49', flag: '🇩🇪', label: 'Germany' },
  { code: '+966', flag: '🇸🇦', label: 'Saudi Arabia' },
  { code: '+60', flag: '🇲🇾', label: 'Malaysia' },
  { code: '+880', flag: '🇧🇩', label: 'Bangladesh' },
  { code: '+92', flag: '🇵🇰', label: 'Pakistan' },
  { code: '+94', flag: '🇱🇰', label: 'Sri Lanka' },
  { code: '+977', flag: '🇳🇵', label: 'Nepal' },
  { code: '+63', flag: '🇵🇭', label: 'Philippines' },
  { code: '+33', flag: '🇫🇷', label: 'France' },
  { code: '+81', flag: '🇯🇵', label: 'Japan' },
  { code: '+86', flag: '🇨🇳', label: 'China' },
  { code: '+27', flag: '🇿🇦', label: 'South Africa' },
];

export const DEFAULT_DIAL_CODE = '+91';

/** Dial codes longest-first, so `+971` matches before `+97`/`+91` before `+9`. */
const DIAL_CODES_BY_LENGTH = [...DIAL_CODES].sort((a, b) => b.code.length - a.code.length);

/** Strip to digits and cap at the E.164 national-part maximum (≤ 14; the dial
 *  code is stored separately, and E.164 total is 15 incl. country code). */
export function sanitizeNationalNumber(input: string): string {
  return input.replace(/\D/g, '').slice(0, 14);
}

/** Combine a dial code + national number into E.164. Empty national → ''. */
export function toE164(dialCode: string, national: string): string {
  const n = sanitizeNationalNumber(national);
  return n ? `${dialCode}${n}` : '';
}

/**
 * Validate a national number for the chosen country. India keeps its strict
 * 10-digit, [6-9]-leading rule; every other country uses a permissive E.164
 * national-number length range so we don't have to encode per-country rules.
 */
export function isValidNationalNumber(dialCode: string, national: string): boolean {
  const n = sanitizeNationalNumber(national);
  if (dialCode === DEFAULT_DIAL_CODE) return /^[6-9]\d{9}$/.test(n);
  return n.length >= 6 && n.length <= 14;
}

/**
 * Split a stored phone value back into { dialCode, national } for prefill.
 * Handles E.164 (`+9198…`), bare national (`98…`), and the legacy India format
 * where the country code was implied or stored as a `91`/`0` prefix.
 */
export function parsePhone(stored: string | null | undefined): { dialCode: string; national: string } {
  const raw = (stored || '').trim();
  if (!raw) return { dialCode: DEFAULT_DIAL_CODE, national: '' };

  if (raw.startsWith('+')) {
    const digits = raw.replace(/\D/g, '');
    const match = DIAL_CODES_BY_LENGTH.find((d) => digits.startsWith(d.code.slice(1)));
    if (match) {
      return { dialCode: match.code, national: sanitizeNationalNumber(digits.slice(match.code.length - 1)) };
    }
    return { dialCode: DEFAULT_DIAL_CODE, national: sanitizeNationalNumber(digits) };
  }

  // No '+': treat as a legacy India national number (strip a 91/0 prefix).
  const national = raw.replace(/\D/g, '').replace(/^91/, '').replace(/^0+/, '');
  return { dialCode: DEFAULT_DIAL_CODE, national: sanitizeNationalNumber(national) };
}
